import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaCoins, FaSave } from 'react-icons/fa';

import { getCurrencySettings, updateCurrencySettings } from '../../services/api';
import { getCurrencyMeta, listCurrencyCodes } from '../../utils/currencyCatalog';
import { setEmpresaCurrency } from '../../utils/currency';

/**
 * Aba "Moeda e Cotação" das Informações da Loja.
 *
 * A loja define em que moeda cadastra os preços, como quer exibi-los ao cliente
 * e — quando exibe duas moedas — qual cotação usar. A cotação é da LOJA: no
 * Líbano ela varia por região, e é o dono quem diz quantas liras valem 1 dólar.
 *
 * O PREVIEW é a parte mais importante desta tela. A cotação multiplica todos os
 * preços do cardápio de uma vez, então um zero a mais passa despercebido e o
 * cliente vê o cardápio errado antes de alguém notar. Não dá para o sistema
 * distinguir 897.000 (erro de digitação) de 897.000 (cotação real num pico de
 * inflação) — apertar a validação bloquearia cotação legítima. Então em vez de
 * restringir, mostramos a consequência: "um prato de $10 sairia por LBP X".
 */
function MoedaConfig() {
    const { t, i18n } = useTranslation('storeInfo');

    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [moedaRegiao, setMoedaRegiao] = useState(null);

    const [form, setForm] = useState({
        habilitado: false,
        base: '',
        exibicao: 'base',
        secundaria: '',
        cotacao: '',
    });
    const [atualizadaEm, setAtualizadaEm] = useState(null);
    const [atualizadaPor, setAtualizadaPor] = useState(null);

    const moedas = useMemo(() => listCurrencyCodes(), []);
    const precisaSecundaria = form.exibicao === 'secundaria' || form.exibicao === 'ambas';

    /**
     * Rótulo da moeda para o dono do restaurante.
     *
     * Não usar Intl.DisplayNames aqui: para LBP ele devolve "Lebanese Pound" /
     * "Libra libanesa", que é a tradução do código ISO — e ninguém no Líbano
     * chama a moeda assim. Confirmado com falante nativo: é "Lira".
     * O nome vem do i18n, com o do catálogo como fallback.
     */
    const rotuloMoeda = useCallback(
        (code) => `${t(`currency.names.${code}`, getCurrencyMeta(code).name)} (${code})`,
        [t]
    );

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const { data } = await getCurrencySettings();
            const m = data?.moeda || {};
            setForm({
                habilitado: m.habilitado === true,
                base: m.base || data?.moeda_da_regiao || '',
                exibicao: m.exibicao || 'base',
                secundaria: m.secundaria || '',
                cotacao: m.cotacao ?? '',
            });
            setAtualizadaEm(m.cotacao_updatedAt || null);
            setAtualizadaPor(m.cotacao_updatedBy || null);
            setMoedaRegiao(data?.moeda_da_regiao || null);
        } catch (error) {
            console.error('[MOEDA] Erro ao carregar configuração:', error);
            toast.error(t('currency.loadError', 'Nao foi possivel carregar a configuracao de moeda.'));
        } finally {
            setCarregando(false);
        }
    }, [t]);

    useEffect(() => { carregar(); }, [carregar]);

    const alterar = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

    /**
     * Preview: quanto custaria um prato de referência nas duas moedas.
     * Usa 10 unidades da moeda base (ex: $10) porque é uma conta que o dono
     * confere de cabeça.
     */
    const preview = useMemo(() => {
        if (!precisaSecundaria) return null;
        const taxa = Number(form.cotacao);
        if (!form.base || !form.secundaria || !Number.isFinite(taxa) || taxa <= 0) return null;

        const metaBase = getCurrencyMeta(form.base);
        const metaSec = getCurrencyMeta(form.secundaria);
        const amostra = 10;

        const fmt = (v, code) =>
            new Intl.NumberFormat(getCurrencyMeta(code).locale, { style: 'currency', currency: code }).format(v);

        return {
            de: fmt(amostra, metaBase.code),
            para: fmt(
                metaSec.step >= 1 ? Math.round(amostra * taxa) : Math.round(amostra * taxa * 100) / 100,
                metaSec.code
            ),
        };
    }, [form.base, form.secundaria, form.cotacao, precisaSecundaria]);

    const salvar = async () => {
        if (form.habilitado) {
            if (!form.base) {
                return toast.warning(t('currency.baseRequired', 'Escolha a moeda dos precos.'));
            }
            if (precisaSecundaria) {
                if (!form.secundaria) {
                    return toast.warning(t('currency.secondaryRequired', 'Escolha a segunda moeda.'));
                }
                if (form.secundaria === form.base) {
                    return toast.warning(t('currency.secondaryMustDiffer', 'A segunda moeda deve ser diferente da principal.'));
                }
                if (!(Number(form.cotacao) > 0)) {
                    return toast.warning(t('currency.rateRequired', 'Informe a cotacao.'));
                }
            }
        }

        setSalvando(true);
        try {
            const payload = form.habilitado
                ? {
                    habilitado: true,
                    base: form.base,
                    exibicao: form.exibicao,
                    ...(precisaSecundaria
                        ? { secundaria: form.secundaria, cotacao: Number(form.cotacao) }
                        : {}),
                }
                : { habilitado: false };

            const { data } = await updateCurrencySettings(payload);

            // Atualiza o contexto ambiente na hora: sem isto o dono salva e
            // continua vendo a moeda antiga em todas as telas ate recarregar.
            setEmpresaCurrency({ configuracoes: { moeda: data?.moeda || payload } });

            toast.success(t('currency.saved', 'Configuracao de moeda salva.'));
            carregar();
        } catch (error) {
            const resp = error?.response?.data;
            if (resp?.faixa_esperada) {
                toast.error(
                    t('currency.rateOutOfRange', 'Cotacao fora da faixa esperada. Confira se nao faltou ou sobrou um zero.')
                );
            } else {
                toast.error(resp?.msg || t('currency.saveError', 'Nao foi possivel salvar.'));
            }
            console.error('[MOEDA] Erro ao salvar:', error);
        } finally {
            setSalvando(false);
        }
    };

    if (carregando) {
        return (
            <div className="modern-loading">
                <div className="modern-spinner"></div>
                <p>{t('loading', 'Carregando...')}</p>
            </div>
        );
    }

    return (
        <div className="info-loja-dados-content">
            <div className="info-loja-dados-section-header">
                <div className="info-loja-dados-header">
                    <div className="info-loja-dados-header-icon"><FaCoins /></div>
                    <div>
                        <h2>{t('currency.title', 'Moeda e Cotacao')}</h2>
                        <p>{t('currency.subtitle', 'Defina em que moeda seus precos estao e como exibi-los ao cliente.')}</p>
                    </div>
                </div>
                <button className="modern-btn modern-btn-save" onClick={salvar} disabled={salvando}>
                    <FaSave /> {salvando ? t('saving', 'Salvando...') : t('save', 'Salvar')}
                </button>
            </div>

            <div className="moeda-config-form">
                {/* Liga/desliga. Desligado = comportamento padrao da regiao. */}
                <label className="moeda-config-toggle">
                    <input
                        type="checkbox"
                        checked={form.habilitado}
                        onChange={(e) => alterar('habilitado', e.target.checked)}
                    />
                    <span>{t('currency.enable', 'Configurar moeda desta loja')}</span>
                </label>

                {!form.habilitado && (
                    <p className="moeda-config-hint">
                        {t('currency.disabledHint', 'Desligado, a loja usa a moeda padrao da regiao')}
                        {moedaRegiao ? ` (${moedaRegiao}).` : '.'}
                    </p>
                )}

                {form.habilitado && (
                    <>
                        <div className="moeda-config-field">
                            <label>{t('currency.base', 'Moeda dos precos')}</label>
                            <select value={form.base} onChange={(e) => alterar('base', e.target.value)}>
                                <option value="">{t('currency.select', 'Selecione...')}</option>
                                {moedas.map((c) => (
                                    <option key={c} value={c}>{rotuloMoeda(c)}</option>
                                ))}
                            </select>
                            <small>{t('currency.baseHint', 'E a moeda em que voce cadastra os precos dos produtos.')}</small>
                        </div>

                        <div className="moeda-config-field">
                            <label>{t('currency.display', 'Como exibir ao cliente')}</label>
                            <select value={form.exibicao} onChange={(e) => alterar('exibicao', e.target.value)}>
                                <option value="base">{t('currency.displayBase', 'Somente a moeda dos precos')}</option>
                                <option value="secundaria">{t('currency.displaySecondary', 'Somente a segunda moeda (convertida)')}</option>
                                <option value="ambas">{t('currency.displayBoth', 'As duas moedas')}</option>
                            </select>
                        </div>

                        {precisaSecundaria && (
                            <>
                                <div className="moeda-config-field">
                                    <label>{t('currency.secondary', 'Segunda moeda')}</label>
                                    <select value={form.secundaria} onChange={(e) => alterar('secundaria', e.target.value)}>
                                        <option value="">{t('currency.select', 'Selecione...')}</option>
                                        {moedas
                                            .filter((c) => c !== form.base)
                                            .map((c) => (
                                                <option key={c} value={c}>{rotuloMoeda(c)}</option>
                                            ))}
                                    </select>
                                </div>

                                <div className="moeda-config-field">
                                    <label>
                                        {t('currency.rate', 'Cotacao')}
                                        {form.base && form.secundaria ? ` — 1 ${form.base} = ? ${form.secundaria}` : ''}
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={form.cotacao}
                                        onChange={(e) => alterar('cotacao', e.target.value)}
                                        placeholder={form.secundaria === 'LBP' ? '89700' : '0'}
                                    />
                                    <small>{t('currency.rateHint', 'Quantas unidades da segunda moeda valem 1 unidade da moeda dos precos. Voce define a cotacao da sua loja.')}</small>
                                </div>

                                {/* Preview -- ver comentario no topo do arquivo. */}
                                {preview && (
                                    <div className="moeda-config-preview">
                                        <strong>{t('currency.previewTitle', 'Confira antes de salvar')}</strong>
                                        <p>
                                            {t('currency.previewLine', 'Um item de {{de}} sera exibido como {{para}}.', {
                                                de: preview.de,
                                                para: preview.para,
                                            })}
                                        </p>
                                        <small>{t('currency.previewHint', 'Se esse valor parecer estranho, confira se nao faltou ou sobrou um zero na cotacao.')}</small>
                                    </div>
                                )}

                                {atualizadaEm && (
                                    <p className="moeda-config-hint">
                                        {t('currency.lastUpdate', 'Cotacao atualizada em {{data}}{{por}}', {
                                            data: new Date(atualizadaEm).toLocaleString(i18n.language),
                                            por: atualizadaPor
                                                ? t('currency.lastUpdateBy', { user: atualizadaPor })
                                                : '',
                                        })}
                                    </p>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default MoedaConfig;
